import { ChecklistField } from "@smartsuite/react-ui/lib/components/fields/Checklist/ChecklistField";
import type {
  ChecklistFieldTexts,
  ChecklistItemContent,
  ChecklistItemEditorRenderProps,
} from "@smartsuite/react-ui/lib/components/fields/Checklist/ChecklistField.types";
import { ChecklistFieldWithTeamAssignment } from "@smartsuite/react-ui/lib/components/fields/Checklist/ChecklistFieldWithTeamAssignment";
import { MemberAvatar } from "@smartsuite/react-ui/lib/components/widgets/MemberAvatar/MemberAvatar";
import { isDocEmpty } from "@smartsuite/smartdoc";
import type { EditorData } from "@smartsuite/types";
import type React from "react";
import { SmartDocRenderer } from "../../../../components/common/SmartDocRenderer/SmartDocRenderer";
import { ErrorBoundary } from "../../../../components/ErrorBoundary/ErrorBoundary";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { texts } from "./ChecklistFieldControl.text";
import { ChecklistItemEditor } from "./ChecklistItemEditor";
import { useChecklistField } from "./useChecklistField";

export type ChecklistFieldControlProps = FormFieldProps;

const checklistFieldTexts: ChecklistFieldTexts = {
  ofText: $t(texts.ofText),
  cancel: $t(texts.cancel),
  add: $t(texts.add),
  update: $t(texts.update),
  deleteItem: $t(texts.deleteItem),
  addNewValue: $t(texts.addNewValue),
  notModifiableTooltip: $t(texts.notModifiableTooltip),
  cantDeleteTooltip: $t(texts.cantDeleteTooltip),
  dueDate: $t(texts.dueDate),
  selectToday: $t(texts.selectToday),
  clearDate: $t(texts.clearDate),
  enterDueDate: $t(texts.enterDueDate),
  dueToday: $t(texts.dueToday),
  dueIn: (relative: string) => $t(texts.dueIn, { relative }),
  overdue: (relative: string) => $t(texts.overdue, { relative }),
  completedOnDueDate: $t(texts.completedOnDueDate),
  completedBeforeDueDate: (relative: string) => $t(texts.completedBeforeDueDate, { relative }),
  completedAfterDueDate: (relative: string) => $t(texts.completedAfterDueDate, { relative }),
  assignTo: $t(texts.assignTo),
  assignedTo: $t(texts.assignedTo),
  assignedToName: (name: string) => $t(texts.assignedToName, { name }),
  removeAssignment: $t(texts.removeAssignment),
  assigneeMembersLabel: $t(texts.assigneeMembersLabel),
  assigneeTeamsLabel: $t(texts.assigneeTeamsLabel),
  search: $t(texts.search),
  noResults: $t(texts.noResults),
};

/**
 * Checklist for the form renderer. Editing is delegated to react-ui, which gates adding new
 * items on `field.params.new_entries_allowed` and editing/deleting seeded items on
 * `field.params.modify_defaults_allowed` — so the field's own settings decide what the
 * respondent may do, and this control only supplies the smartdoc editor and the assignable
 * members. A form-level `read_only` still locks the whole field via `isReadOnly`.
 */
export const ChecklistFieldControl: React.FunctionComponent<ChecklistFieldControlProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  readOnly,
  isItemConditionSatisfied,
}) => {
  const { preferences } = useFormContext();
  const {
    formField,
    members,
    teams,
    isTeamAssignmentEnabled,
    isDueDateEnabled,
    isAssigneeEnabled,
    value,
    appField,
  } = useChecklistField({
    name,
    field,
    formItem,
    isItemConditionSatisfied,
  });

  if (!isItemConditionSatisfied) return null;

  // Shared by both variants; react-ui builds the picker options from the raw members/teams and
  // the host only supplies the access-filtered lists.
  const commonProps = {
    value,
    field: appField,
    texts: checklistFieldTexts,
    members,
    userPreferences: preferences,
    isReadOnly: readOnly,
    showDueDate: isDueDateEnabled,
    showAssignee: isAssigneeEnabled,
    isDocEmpty,
    // Keyed by item identity: when the trailing "add new" row resets to a fresh item the
    // uncontrolled editor must remount empty.
    renderItemEditor: (editorProps: ChecklistItemEditorRenderProps) => (
      <ChecklistItemEditor key={editorProps.itemId} {...editorProps} />
    ),
    renderItemDisplay: (content: ChecklistItemContent) => (
      <SmartDocRenderer editorData={content as unknown as EditorData} />
    ),
    className: "checklist-field-control",
    onChange: formField.onChange,
    onChangeFocused: formField.onChangeFocused,
  };

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      helpText={formItem.params.helpText}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <ErrorBoundary>
        {isTeamAssignmentEnabled ? (
          // The team-aware variant owns its assignee rows, so it takes neither
          // renderMemberAvatar nor renderMemberOption; `resolveMemberImage` is omitted because
          // the forms member endpoint carries no profile photo, which leaves initials.
          <ChecklistFieldWithTeamAssignment {...commonProps} teams={teams} />
        ) : (
          <ChecklistField
            {...commonProps}
            renderMemberAvatar={(member, size) => (
              <MemberAvatar member={member} size={size} enableStatus={false} />
            )}
          />
        )}
      </ErrorBoundary>
    </FormFieldControl>
  );
};
